import { useEffect, useState } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider, useAuth } from "./lib/auth";
import ErrorBoundary from "./components/ErrorBoundary";
import Landing from "./pages/Landing";
import Onboarding from "./pages/Onboarding";
import PhotoUpload from "./pages/PhotoUpload";
import CompleteProfile from "./pages/CompleteProfile";
import Discover from "./pages/Discover";
import Matches from "./pages/Matches";
import Chat from "./pages/Chat";
import Profile from "./pages/Profile";
import Premium from "./pages/Premium";
import Admin from "./pages/Admin";
import StoryView from "./pages/StoryView";
import PublicProfile from "./pages/PublicProfile";
import Blocked from "./pages/Blocked";
import api from "./lib/api";

function Protected({ children }) {
  const { authed, loading, user } = useAuth();
  if (loading) {
    return (
      <div className="kupidon-shell flex items-center justify-center">
        <div className="text-[color:var(--ink-soft)]">Загружаем…</div>
      </div>
    );
  }
  if (!authed) return <Navigate to="/" replace />;
  if (user?.is_active === false) return <Navigate to="/blocked" replace />;
  if (!user?.birth_date) return <Navigate to="/onboarding" replace />;
  if (!user?.photos || user.photos.length === 0) return <Navigate to="/photos" replace />;
  return children;
}

function OnboardGate({ children }) {
  const { authed, loading, user } = useAuth();
  if (loading) {
    return (
      <div className="kupidon-shell flex items-center justify-center">
        <div className="text-[color:var(--ink-soft)]">Загружаем…</div>
      </div>
    );
  }
  if (!authed) return <Navigate to="/" replace />;
  if (user?.is_active === false) return <Navigate to="/blocked" replace />;
  // Don't redirect to /onboarding — we're already there (or on /photos).
  // The Protected gate handles redirecting TO here from other pages.
  return children;
}

function BlockedGate({ children }) {
  const { authed, loading, user } = useAuth();
  if (loading) {
    return (
      <div className="kupidon-shell flex items-center justify-center">
        <div className="text-[color:var(--ink-soft)]">Загружаем…</div>
      </div>
    );
  }
  if (!authed) return <Navigate to="/" replace />;
  if (user?.is_active !== false) {
    if (!user?.birth_date) return <Navigate to="/onboarding" replace />;
    if (!user?.photos || user.photos.length === 0) return <Navigate to="/photos" replace />;
    return <Navigate to="/discover" replace />;
  }
  return children;
}

function AdminGate({ children }) {
  const { authed, loading } = useAuth();
  const [checking, setChecking] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    if (!authed) {
      setChecking(false);
      setIsAdmin(false);
      return;
    }
    let alive = true;
    api.get("/admin/me")
      .then(() => {
        if (!alive) return;
        setIsAdmin(true);
        setChecking(false);
      })
      .catch(() => {
        if (!alive) return;
        setIsAdmin(false);
        setChecking(false);
      });
    return () => {
      alive = false;
    };
  }, [authed]);

  if (loading || checking) {
    return (
      <div className="kupidon-shell flex items-center justify-center">
        <div className="text-[color:var(--ink-soft)]">Загружаем…</div>
      </div>
    );
  }
  if (!authed) return <Navigate to="/" replace />;
  if (!isAdmin) return <Navigate to="/discover" replace />;
  return children;
}

function App() {
  useEffect(() => {
    document.title = "Kupidon REBORN";
  }, []);
  return (
    <AuthProvider>
      <ErrorBoundary>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/blocked" element={<BlockedGate><Blocked /></BlockedGate>} />
            <Route path="/onboarding" element={<OnboardGate><Onboarding /></OnboardGate>} />
            <Route path="/photos" element={<OnboardGate><PhotoUpload /></OnboardGate>} />
            <Route path="/complete-profile" element={<OnboardGate><CompleteProfile /></OnboardGate>} />
            <Route path="/discover" element={<Protected><Discover /></Protected>} />
            <Route path="/matches" element={<Protected><Matches /></Protected>} />
            <Route path="/chat/:matchId" element={<Protected><Chat /></Protected>} />
            <Route path="/profile" element={<Protected><Profile /></Protected>} />
            <Route path="/premium" element={<Protected><Premium /></Protected>} />
            <Route path="/person/:id" element={<Protected><PublicProfile /></Protected>} />
            <Route path="/story/:userId" element={<Protected><StoryView /></Protected>} />
            <Route path="/admin" element={<Admin />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-center" theme="light" richColors closeButton />
      </ErrorBoundary>
    </AuthProvider>
  );
}

export default App;
